import { createContext, useContext, useMemo, useState } from "react";

const STORAGE_KEY = "elva.activeCompanyId";
const YEAR_STORAGE_KEY = "elva.activeYear";
const MIN_YEAR = 2022;
const MAX_YEAR = 2040;

const ActiveCompanyContext = createContext(null);

// App-level company context for company-dependent planning/financial views.
// Persisted in localStorage; each view still validates the id against the
// companies the current user is authorized to see (backend stays authoritative).
export function ActiveCompanyProvider({ children }) {
  const [companyId, setCompanyIdState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });

  const [year, setYearState] = useState(() => {
    try {
      const y = Number(localStorage.getItem(YEAR_STORAGE_KEY));
      return Number.isInteger(y) && y >= MIN_YEAR && y <= MAX_YEAR ? y : null;
    } catch {
      return null;
    }
  });

  const setCompanyId = (id) => {
    const value = id || "";
    setCompanyIdState(value);
    try {
      if (value) localStorage.setItem(STORAGE_KEY, value);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // storage unavailable (private mode) — keep in-memory state only
    }
  };

  const setYear = (y) => {
    const value = y == null ? null : Number(y);
    setYearState(value);
    try {
      if (value == null) localStorage.removeItem(YEAR_STORAGE_KEY);
      else localStorage.setItem(YEAR_STORAGE_KEY, String(value));
    } catch {
      // storage unavailable (private mode) — keep in-memory state only
    }
  };

  const value = useMemo(() => ({ companyId, setCompanyId, year, setYear }), [companyId, year]);
  return (
    <ActiveCompanyContext.Provider value={value}>
      {children}
    </ActiveCompanyContext.Provider>
  );
}

// Deterministic ActiveYear resolution shared by all year-based views: keep the
// active year when the selected company (or hub) has created it; otherwise fall
// back to the current calendar year if it exists, else the latest existing
// year. Never invents a year that has no periods.
export function pickActiveYear(validYears, activeYear) {
  if (!validYears || validYears.length === 0) return null;
  if (activeYear != null && validYears.includes(activeYear)) return activeYear;
  const current = new Date().getFullYear();
  if (validYears.includes(current)) return current;
  return Math.max(...validYears);
}

export function useActiveCompany() {
  const ctx = useContext(ActiveCompanyContext);
  if (!ctx) throw new Error("useActiveCompany must be used within ActiveCompanyProvider");
  return ctx;
}
